import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAgencyBySlug } from '../../utils/slugUtils';
import { CANONICAL_DOMAIN } from '../../lib/canonicalUrl';

/**
 * SubdomainRedirect - Handles subdomain detection and redirects BEFORE any other routing
 * This component should be used as the first route to catch subdomain requests
 */
const SubdomainRedirect: React.FC = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkSubdomain = async () => {
      const hostname = window.location.hostname;
      const subdomain = getSubdomainFromHostname(hostname);
      
      console.log('SubdomainRedirect: Checking hostname:', hostname);
      console.log('SubdomainRedirect: Extracted subdomain:', subdomain);
      
      if (subdomain && !isReservedSubdomain(subdomain)) {
        console.log('SubdomainRedirect: Valid subdomain detected:', subdomain);
        
        // Check if subdomain corresponds to an agency
        const agency = await getAgencyBySlug(subdomain);
        console.log('SubdomainRedirect: Agency lookup result:', agency);
        
        if (agency) {
          // Get user's preferred language
          let preferredLang = 'fr';
          const storedLang = localStorage.getItem('i18nextLng');
          if (storedLang && ['en', 'fr', 'ar'].includes(storedLang)) {
            preferredLang = storedLang;
          }
          
          // Determine base URL (localhost for development, canonical domain for production)
          const isLocalhost = hostname.includes('localhost');
          const baseUrl = isLocalhost ? `http://localhost:5173` : CANONICAL_DOMAIN;
          
          // Build redirect URL to main domain
          const currentPath = window.location.pathname;
          const currentSearch = window.location.search;
          const targetPath = `/${preferredLang}/${subdomain}`;
          
          // If we're already on the correct path, don't redirect
          if (currentPath === targetPath || currentPath.startsWith(targetPath + '/')) {
            console.log('SubdomainRedirect: Already on correct path, no redirect needed');
            setIsChecking(false);
            return;
          }
          
          // Redirect to main domain with agency slug
          const redirectUrl = `${baseUrl}${targetPath}${currentSearch}`;
          console.log('SubdomainRedirect: Redirecting subdomain to main domain:', redirectUrl);
          window.location.href = redirectUrl;
          return;
        } else {
          console.log('SubdomainRedirect: No agency found for subdomain:', subdomain);
        }
      } else {
        console.log('SubdomainRedirect: No valid subdomain detected or reserved subdomain');
      }
      
      setIsChecking(false);
    };

    checkSubdomain();
  }, [navigate]);

  // Show loading while checking subdomain
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  // If no subdomain redirect needed, redirect to home with language
  const savedLanguage = localStorage.getItem('kiracar-language') || 'fr';
  navigate(`/${savedLanguage}`, { replace: true });
  return null;
};

/**
 * Extract subdomain from hostname
 */
function getSubdomainFromHostname(hostname: string): string | null {
  // Remove www. prefix if present and handle port numbers
  const cleanHostname = hostname.replace(/^www\./, '').split(':')[0];
  
  console.log('getSubdomainFromHostname: Original hostname:', hostname);
  console.log('getSubdomainFromHostname: Clean hostname:', cleanHostname);
  
  // Check if it's a subdomain of kiracar.com (production)
  if (cleanHostname.endsWith('.kiracar.com')) {
    const subdomain = cleanHostname.replace('.kiracar.com', '');
    console.log('getSubdomainFromHostname: Production subdomain:', subdomain);
    // Only return subdomain if it's not the main domain
    if (subdomain && subdomain !== 'kiracar') {
      return subdomain;
    }
  }
  
  // Check if it's a subdomain of localhost (development)
  if (cleanHostname.includes('.localhost')) {
    const parts = cleanHostname.split('.localhost');
    console.log('getSubdomainFromHostname: Localhost parts:', parts);
    if (parts.length >= 2) {
      const subdomain = parts[0];
      console.log('getSubdomainFromHostname: Localhost subdomain:', subdomain);
      // Only return subdomain if it's not the main domain
      if (subdomain && subdomain !== 'localhost') {
        return subdomain;
      }
    }
  }
  
  console.log('getSubdomainFromHostname: No subdomain found');
  return null;
}

/**
 * Check if subdomain is reserved
 */
function isReservedSubdomain(subdomain: string): boolean {
  const reservedSubdomains = [
    'www', 'api', 'admin', 'app', 'mail', 'ftp', 'blog', 'help', 'support',
    'about', 'contact', 'terms', 'privacy', 'login', 'signup', 'register',
    'dashboard', 'profile', 'settings', 'cars', 'agencies', 'brands', 'cities',
    'locations', 'search', 'book', 'booking', 'bookings', 'payment', 'payments',
    'invoice', 'invoices', 'contract', 'contracts', 'review', 'reviews',
    'notification', 'notifications', 'kiracar', 'kira', 'car', 'rental', 'rent',
    'location', 'voiture', 'staging', 'dev', 'test', 'demo'
  ];
  
  return reservedSubdomains.includes(subdomain.toLowerCase());
}

export default SubdomainRedirect;
